import type { FragmentOf } from '~publish/graphql'
import type {
  PlanContent_Billing,
  LegacyPlanContent_Billing,
} from './PlanContent'

/**
 * Type guard to check if the billing data is legacy.
 * @param data - The billing data to check.
 * @returns True if the billing data is legacy, false otherwise.
 */
export const isLegacyBilling = (
  data?:
    | FragmentOf<typeof PlanContent_Billing>
    | FragmentOf<typeof LegacyPlanContent_Billing>
    | null,
): data is FragmentOf<typeof LegacyPlanContent_Billing> => {
  return !!data && 'subscriptions' in data
}

/**
 * Formats the user limit copy.
 * @param userLimit - The user limit.
 * @returns The formatted user limit copy.
 */
export const getUsersCopy = (userLimit: number): string => {
  let userLimitCopy
  switch (userLimit) {
    case 0:
      userLimitCopy = 'Limited to 1'
      break
    case 9999:
      userLimitCopy = 'Unlimited'
      break
    default:
      userLimitCopy = `Limited to ${userLimit}`
  }
  return userLimitCopy
}

/**
 * Gets the channel pricing description based on the plan type and interval.
 * @param plan - The subscription plan.
 * @param isOnTrial - Whether the user is on a trial.
 * @param isOnTieredPricing - Whether the user is on tiered pricing.
 * @returns The formatted channel pricing description.
 */
export const getChannelPricingDescription = ({
  plan,
  isOnTrial,
  isOnTieredPricing,
}: {
  plan: {
    id: 'agency' | 'essentials' | 'free' | 'team'
    interval: 'month' | 'year'
    prices: {
      basePlanPrice: number | null
      baseMonthlyPrice: number | null
      monthlyFlatFee: number | null
      monthlyChannelPrice: number | null
      channelTierUnit: number | null
    }
  }
  isOnTrial: boolean
  isOnTieredPricing: boolean
}): string => {
  if (isOnTrial) {
    return 'Unlimited'
  }

  if (isOnTieredPricing) {
    return 'Custom pricing'
  }

  if (
    plan.id === 'agency' &&
    plan.prices.monthlyFlatFee &&
    plan.prices.channelTierUnit &&
    plan.prices.monthlyChannelPrice
  ) {
    return `$${plan.prices.monthlyFlatFee} for ${plan.prices.channelTierUnit} channels per month. Additional channels for $${plan.prices.monthlyChannelPrice} per month.`
  }

  return plan.interval === 'year'
    ? `$${plan.prices.basePlanPrice} each per year`
    : `$${plan.prices.baseMonthlyPrice} each per month`
}
